/**
 * Google Analytics 4 — client-side funnel events (gtag.js).
 *
 * Complements the server-side Measurement Protocol purchase (server/lib/ga4.js).
 * Our checkout completes off-Shopify on a different domain, so the storefront's
 * GA4 tag never sees the mid-funnel — begin_checkout / add_shipping_info /
 * add_payment_info were effectively dead in GA4 (verified: ~1-2 events / 2 wks).
 * This module loads the Google tag on the checkout and fires those events so
 * they reach GA4, and through the GA4↔Ads link feed audiences / remarketing /
 * Performance Max signals.
 *
 * The key to making these events useful for remarketing is the client_id: we
 * override gtag's client_id with the one the storefront forwarded in `_ga`, so
 * the checkout events attach to the SAME GA4 user as the customer's storefront
 * session. They land in the same audiences and the funnel stitches together
 * across the two domains; without the override the checkout (a different
 * origin) would mint a brand-new client_id and the events would float free.
 *
 * Purchase stays server-side ONLY (Measurement Protocol). Firing it here too
 * would risk double-counting — GA4 dedups purchase by transaction_id within a
 * single transport, not across MP + gtag.
 *
 * Config-driven & per-shop, exactly like the Meta pixel (src/lib/pixel.js): the
 * public GA4 measurement ID comes from /api/pixel-config?shop=<domain>. Shops
 * without a GA4 config (the kenvit-3m test store) get nothing → every call is a
 * silent no-op.
 */

let measurementId = null;
let itemIdPrefix = '';   // e.g. "shopify_UA_" → item_id "shopify_UA_<pid>_<vid>"
let initStarted = false;
let ready = false;

/**
 * Parse the GA4 client_id from a raw `_ga` cookie value.
 * "GA1.1.<a>.<b>" → "<a>.<b>". Returns '' if unparseable. Mirrors
 * parseGaClientId() in server/lib/ga4.js so client + server stitch to the
 * same GA4 user.
 */
export function parseGaClientId(rawGa) {
  if (!rawGa) return '';
  const s = String(rawGa);
  const full = s.match(/^GA\d+\.\d+\.(\d+\.\d+)$/);
  if (full) return full[1];
  const tail = s.match(/(\d+\.\d+)$/); // already a bare "<a>.<b>"
  return tail ? tail[1] : '';
}

/** Load gtag.js exactly once. Defines window.gtag + dataLayer. */
function injectBase(id) {
  if (window.gtag) return;
  window.dataLayer = window.dataLayer || [];
  // gtag pushes the literal `arguments` object — must not be an arrow fn.
  window.gtag = function () { window.dataLayer.push(arguments); };
  const s = document.createElement('script');
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(id)}`;
  document.head.appendChild(s);
}

/**
 * Initialise the Google tag for a shop and fire the automatic page_view.
 * Idempotent. Resolves `true` when GA4 is configured + active for the shop,
 * `false` otherwise (unconfigured shop or fetch failure → all events no-op).
 *
 * @param {string} shop
 * @param {object} [opts]
 * @param {string} [opts.clientId] GA4 client_id parsed from the forwarded `_ga`
 */
export async function initGtag(shop, { clientId } = {}) {
  if (initStarted) return ready;
  initStarted = true;
  try {
    const res = await fetch(`/api/pixel-config?shop=${encodeURIComponent(shop || '')}`);
    const cfg = res.ok ? await res.json() : {};
    if (!cfg.ga4MeasurementId) return false;
    measurementId = cfg.ga4MeasurementId;
    itemIdPrefix = cfg.ga4ItemIdPrefix || '';
    injectBase(measurementId);
    window.gtag('js', new Date());
    const config = {};
    // Stitch to the storefront GA4 user so checkout events share its audiences.
    if (clientId) config.client_id = clientId;
    window.gtag('config', measurementId, config);
    ready = true;
    return true;
  } catch (_) {
    return false;
  }
}

/** Fire a GA4 event when the tag is active; silent no-op otherwise. */
export function gtagEvent(name, params = {}) {
  if (!ready || !window.gtag) return;
  window.gtag('event', name, params);
}

/** Trailing numeric id from a Shopify GID (also passes bare numerics through). */
function gidToId(gid) {
  const m = String(gid || '').match(/(\d+)\s*$/);
  return m ? m[1] : '';
}

/**
 * Build the GA4 `item_id` that matches the Google Merchant feed offer id.
 * Shopify's Google channel keys each offer as "<prefix><productId>_<variantId>"
 * (e.g. "shopify_UA_8904901263662_47529495396654"), so to make GA4/Ads
 * product-level optimization + dynamic remarketing match the feed we must send
 * the SAME id — NOT the bare variant id (that's Meta's retailer_id).
 * Falls back to the bare variant id (then SKU) when the prefix isn't configured
 * or the product id is missing, preserving the old behaviour for unconfigured
 * shops.
 */
export function googleItemId(productGid, variantGid, sku) {
  const vid = gidToId(variantGid);
  const pid = gidToId(productGid);
  if (itemIdPrefix && pid && vid) return `${itemIdPrefix}${pid}_${vid}`;
  return vid || sku || '';
}

/**
 * Build the GA4 `items` array from checkout cart lines. item_id matches the
 * Google Merchant feed offer id (see googleItemId).
 */
export function itemsFromCartLines(lines = []) {
  return lines.map((l) => ({
    item_id:   googleItemId(l.productId, l.variantId, l.sku),
    item_name: l.name || '',
    quantity:  l.qty,
    price:     l.price,
  }));
}
